import ServiceUnavailableAlert from 'components/core/ServiceUnavailableAlert';
import { addMonths, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  ListGroup,
  ProgressBar,
  Spinner,
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { IoMdClipboard, IoMdPerson } from 'react-icons/io';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectPatient } from 'redux/features/auth/authSlice';
import { buscaAgendasAbertas } from 'services/agenda';
import api from 'services/api';
import { isInfrastructureError } from 'utils/apiErrors';
import { formatDateISO } from 'utils/helpers';

// --- PASSO 1: CONVÊNIO ---
function StepConvenio({ setConvenio, next }) {
  const conveniosAgenda = [
    { codigo: 9, descricao: 'ACE' },
    { codigo: 20, descricao: 'AMAFRESP' },
    { codigo: 140, descricao: 'AMIL' },
    { codigo: 29, descricao: 'APEOESP' },
    { codigo: 12, descricao: 'CABESP' },
    { codigo: 4, descricao: 'CASSI' },
    { codigo: 6, descricao: 'ECONOMUS' },
    { codigo: 180, descricao: 'FUNDAÇÃO CESP' },
    { codigo: 76, descricao: 'IAMSP' },
    { codigo: 1, descricao: 'PARTICULAR' },
    { codigo: 162, descricao: 'MEDIAL' },
    { codigo: 17, descricao: 'SINDICATO COMERCIO VAREJISTA DE MARÍLIA' },
    { codigo: 18, descricao: 'SINDICATO DOS EMPREGADOS DO COMÉRCIO DEMARÍLIA' },
    { codigo: 10, descricao: 'SINDICATO DA SAÚDE DE MÁRILIA' },
    { codigo: 8, descricao: 'SINDICATO SERVIDORES PÚBLICOS DE POMPÉIA' },
    { codigo: 7, descricao: 'SAÚDE CAIXA' },
    { codigo: 2, descricao: 'UNIMED' },
  ];

  function handleConvenioClick(convenio) {
    setConvenio(convenio);
    next();
  }

  return (
    <Card border="primary" className="shadow-sm">
      <Card.Header as="h5" className="bg-primary text-white">
        1. Escolha seu Convênio
      </Card.Header>
      <ListGroup variant="flush">
        {conveniosAgenda.map((convenio) => (
          <ListGroup.Item
            key={convenio.codigo}
            action
            onClick={() => handleConvenioClick(convenio)}
            className="py-2 fw-medium"
          >
            {convenio.descricao.toUpperCase()}
          </ListGroup.Item>
        ))}
      </ListGroup>
    </Card>
  );
}

// --- PASSO 2: MÉDICO ---
function StepMedico({ convenio, setMedico, next }) {
  const medicosAgenda = [
    { codigo: 1, nome: 'Dra. ANA PAULA' },
    { codigo: 2, nome: 'Dr. BENITO' },
    { codigo: 3, nome: 'Dr. ERALDO' },
    { codigo: 4, nome: 'Dra. LUCIENNE' },
    { codigo: 5, nome: 'Dra. MARIA CRISTINA' },
    { codigo: 1233, nome: 'Dra. ROBERTA' },
    { codigo: 1265, nome: 'Dra. JOYCE' },
  ];

  const doctorMap = {
    37: [1],
    1: [2, 5, 3, 4],
    2: [2, 5, 3, 4],
    4: [2, 5, 3, 4],
    6: [2, 5, 3, 4],
    7: [2, 5, 3, 4],
    8: [2, 5, 3, 4],
    9: [2, 5, 3, 4],
    10: [2, 5, 3, 4],
    12: [2, 5, 3, 4],
    17: [2, 5, 3, 4],
    18: [2, 5, 3, 4],
    20: [2, 5, 3, 4],
    29: [2, 5, 3, 4],
    140: [2, 5, 3, 4],
    162: [2, 5, 3, 4],
    180: [2, 5, 3, 4],
    76: [1265, 1233],
  };

  const allowedCodes = doctorMap[convenio?.codigo] || [];
  const doctors = medicosAgenda.filter((doc) => allowedCodes.includes(doc.codigo));

  function handleMedicoClick(medico) {
    setMedico(medico);
    next();
  }

  return (
    <Card border="primary" className="shadow-sm">
      <Card.Header as="h5" className="bg-primary text-white">
        2. Escolha seu Médico
      </Card.Header>
      <ListGroup variant="flush">
        {doctors.length > 0 ? (
          doctors.map((d) => (
            <ListGroup.Item
              key={d.codigo}
              action
              onClick={() => handleMedicoClick(d)}
              className="d-flex align-items-center py-2"
            >
              <IoMdPerson className="me-2 text-primary" size={20} />
              <span className="fw-medium">{d.nome.toUpperCase()}</span>
            </ListGroup.Item>
          ))
        ) : (
          <div className="p-3">
            <Alert variant="warning" className="mb-0">
              Nenhum médico disponível para o convênio selecionado.
            </Alert>
          </div>
        )}
      </ListGroup>
    </Card>
  );
}

// --- PASSO 3: HORÁRIO ---
function verificarHorarioMedico(dia, horario, med) {
  const horariosPorMedico = {
    2: { 2: [1030, 1500], 3: [900, 1600], 4: [830], 5: [1100, 1600], 6: [1000, 1530] },
    3: { 2: [1030, 1700], 3: [1100, 1630], 4: [1130, 1730], 5: [1030, 1800], 6: [1000, 1600] },
    5: { 3: [1400, 1630], 4: [1500, 1700], 5: [1500, 1730], 6: [1430, 1700] },
    1233: { 2: [1330, 1500], 3: [1400, 1700], 5: [1500, 1600], 6: [1600, 1700] },
    1265: { 3: [830, 1030], 4: [1430, 1700] },
  };

  return horariosPorMedico[med]?.[dia]?.includes(horario);
}

// --- PASSO 3: HORÁRIO ---
function StepHorario({ medico, setAgendaSelecionada, next }) {
  const [dateAgenda, setDateAgenda] = useState(new Date());
  const [agendamentos, setAgendamentos] = useState([]);
  const [errorAgenda, setErrorAgenda] = useState(null);
  const [loadingAgenda, setLoadingAgenda] = useState(false);
  const dateAgendaFmt = useMemo(() => formatDateISO(dateAgenda), [dateAgenda]);

  const loadAgenda = useCallback(async () => {
    if (!medico?.codigo) {
      setAgendamentos([]);
      setErrorAgenda(null);
      return;
    }

    setLoadingAgenda(true);
    setErrorAgenda(null);

    try {
      const agendas = await buscaAgendasAbertas({
        codMed: medico.codigo,
        dtAgenda: dateAgendaFmt,
      });

      const filtradas = agendas?.filter((a) =>
        verificarHorarioMedico(a.diaDaSemana, a.HORARIO, a.CODMED)
      );

      setAgendamentos(filtradas || []);
    } catch (err) {
      setErrorAgenda(err);
      if (!isInfrastructureError(err)) {
        toast.error('Erro ao buscar agendas abertas.');
      }
    } finally {
      setLoadingAgenda(false);
    }
  }, [medico, dateAgendaFmt]);

  useEffect(() => {
    loadAgenda();
  }, [loadAgenda]);

  function handleHorarioClick(agenda) {
    setAgendaSelecionada(agenda);
    next();
  }

  const temHorarios = agendamentos.length > 0;
  const erroInfraestrutura = isInfrastructureError(errorAgenda);

  return (
    <Card border="primary" className="shadow-sm">
      <Card.Header as="h5" className="bg-primary text-white">
        3. Escolha Data e Horário
      </Card.Header>
      <Card.Body>
        <div className="d-flex justify-content-center mb-4">
          <DatePicker
            selected={dateAgenda}
            onChange={(dt) => setDateAgenda(dt)}
            inline
            locale={ptBR}
            minDate={new Date()}
            maxDate={addMonths(new Date(), 12)}
          />
        </div>

        <h6 className="mb-3 text-muted">Horários Disponíveis:</h6>
        {loadingAgenda && !temHorarios ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <>
            {errorAgenda &&
              (erroInfraestrutura ? (
                <ServiceUnavailableAlert
                  error={errorAgenda}
                  stale={temHorarios}
                  onRetry={loadAgenda}
                />
              ) : (
                <Alert variant="danger" className="text-center">
                  Erro ao buscar agendas abertas.
                  <div className="mt-2">
                    <Button variant="outline-danger" size="sm" onClick={loadAgenda}>
                      Tentar novamente
                    </Button>
                  </div>
                </Alert>
              ))}

            {temHorarios ? (
              <ListGroup>
                {agendamentos.map((agenda) => (
                  <ListGroup.Item
                    key={agenda.CODAGENDA}
                    action
                    onClick={() => handleHorarioClick(agenda)}
                    className="d-flex align-items-center justify-content-between"
                  >
                    <div className="d-flex align-items-center">
                      <IoMdClipboard className="me-2 text-primary" size={20} />
                      <span>{agenda.hora}</span>
                    </div>
                    <Badge bg="info">{agenda.ATIVIDADE}</Badge>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            ) : !errorAgenda ? (
              <Alert variant="warning" className="text-center mb-0">
                Nenhum horário de consulta disponível para este dia!
              </Alert>
            ) : null}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

// --- PASSO 4: CONFIRMAÇÃO ---
function StepConfirmacao({
  agendaSelecionada,
  convenio,
  medico,
  goToStep,
  patientInfo,
  setConfirmacaoData,
  setError422,
  error422,
}) {
  const patient = patientInfo || useSelector(selectPatient);

  async function handleConfirm(e) {
    e.preventDefault();
    try {
      const agendamento = {
        codAgenda: agendaSelecionada.CODAGENDA,
        codConv: convenio.codigo,
        codPac: patient.codPac || patient.CODPAC,
        codMed: medico.codigo,
        codProcedi: 1,
        dataAgenda: parseISO(agendaSelecionada.DATAAGENDA),
        horario: agendaSelecionada.HORARIO,
        situacao: 'Agendada',
        agendada: 'Agendada',
        atividade: 'CONSULTA',
      };

      const res = await api.put('agenda', agendamento);
      // Store the successful response for the result step
      setConfirmacaoData(res.data);
      // Advance to the confirmation result step (5)
      goToStep(5);
    } catch (error) {
      if (error.response?.status === 422) {
        const apiMessage =
          error.response?.data?.error ||
          error.response?.data?.message ||
          'Erro ao confirmar agendamento';
        setError422(apiMessage);
      } else {
        toast.error(`Falha na Confirmação: ${error.message}`);
      }
    }
  }

  return (
    <Card bg="light" className="shadow-sm border">
      <Card.Body className="p-4">
        {error422 && (
          <Alert variant="warning" dismissible onClose={() => setError422(null)}>
            {error422}
          </Alert>
        )}
        <Card.Title as="h4" className="text-center mb-4 text-primary">
          4. Confirmar Agendamento
        </Card.Title>

        <div className="bg-white p-3 rounded border mb-4">
          <p className="mb-2">
            <strong>Paciente: </strong> {patient?.nome || patient?.NOMEPAC || 'Paciente conectado'}
          </p>
          <p className="mb-2">
            <strong>Médico: </strong> {medico?.nome?.toUpperCase() ?? 'Não informado'}
          </p>
          <p className="mb-2">
            <strong>Convênio: </strong> {convenio?.descricao?.toUpperCase() ?? 'Não informado'}
          </p>
          <p className="mb-2">
            <strong>Data: </strong> {agendaSelecionada?.dataFormatada || 'N/D'}
          </p>
          <p className="mb-0">
            <strong>Horário: </strong> {agendaSelecionada?.hora || 'N/D'}
          </p>
        </div>

        <div className="d-flex justify-content-center gap-2">
          <Button variant="outline-danger" onClick={() => goToStep(1)}>
            Cancelar / Recomeçar
          </Button>
          <Button variant="success" onClick={handleConfirm}>
            Finalizar Agendamento
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}
function StepConfirmacaoResultado({
  data,
  goToStep,
  agendaSelecionada,
  convenio,
  medico,
  patientInfo,
}) {
  const { message } = data || {};
  const patient = patientInfo || useSelector(selectPatient);
  return (
    <Card bg="light" className="shadow-sm border">
      <Card.Body className="p-4">
        <Card.Title as="h4" className="text-center mb-4 text-primary">
          Agendamento Confirmado
        </Card.Title>
        {message && (
          <Alert variant="success" className="mb-3">
            {message}
          </Alert>
        )}
        <div className="bg-white p-3 rounded border mb-4">
          <p className="mb-2">
            <strong>Paciente:</strong> {patient?.nome || patient?.NOMEPAC || 'Paciente conectado'}
          </p>
          <p className="mb-2">
            <strong>Data:</strong>{' '}
            {agendaSelecionada?.dataFormatada || agendaSelecionada?.DATAAGENDA || 'N/D'}
          </p>
          <p className="mb-2">
            <strong>Horário:</strong>{' '}
            {agendaSelecionada?.hora || agendaSelecionada?.HORARIO || 'N/D'}
          </p>
          <p className="mb-2">
            <strong>Médico:</strong> {medico?.nome?.toUpperCase() || 'Não informado'}
          </p>
          <p className="mb-2">
            <strong>Convênio:</strong> {convenio?.descricao?.toUpperCase() || 'Não informado'}
          </p>
        </div>
        <div className="d-flex justify-content-center gap-2">
          <Button variant="primary" onClick={() => goToStep(1)}>
            Novo Agendamento
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}

// --- COMPONENTE PRINCIPAL ---
export default function AgendamentoOnline({ patientInfo }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [convenio, setConvenio] = useState(null);
  const [medico, setMedico] = useState(null);
  const [agendaSelecionada, setAgendaSelecionada] = useState(null);
  const [localPatientInfo, setLocalPatientInfo] = useState(null);
  const [confirmacaoData, setConfirmacaoData] = useState(null);
  const [error422, setError422] = useState(null);
  const effectivePatientInfo = patientInfo || localPatientInfo;

  // When patientInfo is set (via prop), move to the first step (convenio)
  useEffect(() => {
    if (effectivePatientInfo) {
      setCurrentStep(1);
    }
  }, [effectivePatientInfo]);

  const totalSteps = 5;

  const nextStep = () => setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));
  const goToStep = (step) => setCurrentStep(step);

  return (
    <Container className="py-3" style={{ maxWidth: '650px' }}>
      {/* Barra de Progresso Customizada */}
      <div className="mb-3">
        <div className="d-flex justify-content-between mb-1 text-muted small fw-bold">
          <span>
            Passo {currentStep} de {totalSteps}
          </span>
          <span>{Math.round((currentStep / totalSteps) * 100)}% Concluído</span>
        </div>
        <ProgressBar now={(currentStep / totalSteps) * 100} style={{ height: '8px' }} />
      </div>

      {/* Renderização condicional do Passo Ativo */}
      {currentStep === 1 && <StepConvenio setConvenio={setConvenio} next={nextStep} />}
      {currentStep === 2 && (
        <StepMedico convenio={convenio} setMedico={setMedico} next={nextStep} />
      )}
      {currentStep === 3 && (
        <StepHorario medico={medico} setAgendaSelecionada={setAgendaSelecionada} next={nextStep} />
      )}
      {currentStep === 4 && (
        <StepConfirmacao
          agendaSelecionada={agendaSelecionada}
          convenio={convenio}
          medico={medico}
          goToStep={goToStep}
          patientInfo={patientInfo}
          setConfirmacaoData={setConfirmacaoData}
          setError422={setError422}
          error422={error422}
        />
      )}
      {currentStep === 5 && confirmacaoData && (
        <StepConfirmacaoResultado
          data={confirmacaoData}
          goToStep={goToStep}
          agendaSelecionada={agendaSelecionada}
          convenio={convenio}
          medico={medico}
          patientInfo={patientInfo}
        />
      )}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <Button variant="secondary" size="sm" onClick={prevStep} disabled={currentStep === 1}>
          Anterior
        </Button>
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={nextStep}
            disabled={
              currentStep === totalSteps ||
              (currentStep === 1 && !convenio) ||
              (currentStep === 2 && !medico) ||
              (currentStep === 3 && !agendaSelecionada)
            }
          >
            Próximo
          </Button>
        </div>
      </div>
    </Container>
  );
}
